import { useState, useEffect, useRef } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { Sparkles, Maximize2 } from 'lucide-react';
import api from '../api';
import { logger } from '../utils/logger';
import {
  FocusHeader, FocusTimerPanel, FocusContextPanel,
  FocusChecklist, FocusCoachReminder,
} from './focus/FocusModeParts';

export default function FocusMode({ task, onClose, onComplete }) {
  const { preferences } = usePreferences();
  const initialSeconds = (task?.estimatedMinutes || preferences.focusBlockMinutes || 25) * 60;
  const [timeLeft, setTimeLeft] = useState(initialSeconds);
  const [totalTime] = useState(initialSeconds);
  const [isPaused, setIsPaused] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [subtasks, setSubtasks] = useState(task?.subtasks || []);
  const [notes, setNotes] = useState('');
  const intervalRef = useRef(null);
  const { getBusinessColor, getBusinessShortCode } = useBusiness();

  const bizColor = task?.businessId ? getBusinessColor(task.businessId) : 'var(--pd-focus)';
  const bizCode = task?.businessId ? getBusinessShortCode(task.businessId) : '';

  useEffect(() => {
    if (isPaused || timeLeft <= 0) return undefined;
    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [isPaused, timeLeft]);

  const handleComplete = async () => {
    try {
      await api.put(`/tasks/${task.id}`, {
        status: 'COMPLETED',
        actualMinutes: Math.round((totalTime - timeLeft) / 60),
      });
      onComplete?.();
    } catch (err) {
      logger.error('Task completion failed:', err);
    }
    onClose();
  };

  const toggleSubtask = (index) => {
    const next = subtasks.map((item, idx) => idx === index ? { ...item, completed: !item.completed } : item);
    setSubtasks(next);
    // Persist so checklist progress survives Focus Mode and feeds the
    // subtask progress rings on task rows.
    api.put(`/tasks/${task.id}`, { subtasks: next })
      .then(() => {
        try { window.dispatchEvent(new CustomEvent('polkdesq:data-mutated', { detail: { reason: 'subtask-toggle' } })); } catch { /* ignore */ }
      })
      .catch((err) => logger.error('Subtask sync failed:', err));
  };

  // When minimized, the modal is hidden but the timer keeps ticking and
  // the underlying row's `.pd-focus-active` indigo glow becomes visible
  // (the corresponding row is highlighted by TodayView/TasksView). A small
  // floating pill at the bottom-right lets the user restore the modal.
  if (isMinimized) {
    const m = Math.floor(timeLeft / 60);
    const s = timeLeft % 60;
    const remaining = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return (
      <button
        type="button"
        data-testid="focus-restore-pill"
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-6 right-6 z-50 px-4 py-2.5 rounded-full flex items-center gap-3 transition-all"
        style={{
          background: 'var(--pd-surface-3, var(--pd-popover))',
          border: '1px solid var(--pd-focus-color, var(--pd-focus))',
          boxShadow: 'var(--pd-shadow-float, var(--pd-shadow-3))',
          backdropFilter: 'blur(20px) saturate(160%)',
          WebkitBackdropFilter: 'blur(20px) saturate(160%)',
          color: 'var(--pd-text)',
        }}
        title="Resume focus view"
        aria-label="Resume focus view"
      >
        <span
          className="w-2 h-2 rounded-full"
          style={{
            background: 'var(--pd-focus-color, var(--pd-focus))',
            boxShadow: '0 0 8px var(--pd-focus-color, var(--pd-focus))',
            animation: isPaused ? 'none' : 'pd-live-dot-breathe 2.4s ease-in-out infinite',
          }}
        />
        <Sparkles className="w-3.5 h-3.5" style={{ color: bizColor }} />
        <span className="text-[12px] font-semibold tabular-nums">{remaining}</span>
        <span className="text-[11px] max-w-[180px] truncate" style={{ color: 'var(--pd-muted)' }}>{task?.title}</span>
        <Maximize2 className="w-3.5 h-3.5" style={{ color: 'var(--pd-subtle)' }} />
      </button>
    );
  }

  return (
    <div data-testid="focus-mode" className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: 'var(--pd-overlay)', backdropFilter: 'blur(26px)' }}>
      <div className="w-full max-w-5xl rounded-[32px] p-6 md:p-8" style={{ background: 'var(--pd-popover)', border: '1px solid var(--pd-popover-border)', boxShadow: 'var(--pd-shadow-3)' }}>
        <FocusHeader task={task} bizColor={bizColor} onClose={onClose} onMinimize={() => setIsMinimized(true)} />
        <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
          <FocusTimerPanel
            minutes={Math.floor(timeLeft / 60)}
            seconds={timeLeft % 60}
            totalMinutes={Math.floor(totalTime / 60)}
            progress={1 - (timeLeft / totalTime)}
            bizColor={bizColor}
            bizCode={bizCode}
            isPaused={isPaused}
            onTogglePause={() => setIsPaused((prev) => !prev)}
            onComplete={handleComplete}
            onSkip={onClose}
          />
          <div className="grid gap-4 content-start">
            <FocusContextPanel task={task} notes={notes} setNotes={setNotes} />
            <FocusChecklist subtasks={subtasks} onToggle={toggleSubtask} />
            <FocusCoachReminder />
          </div>
        </div>
      </div>
    </div>
  );
}
